/**
 * @file apps/web/src/components/SubmitForApprovalPanel.tsx
 * @description Small wrapper around the existing RoutingBuilder that lets
 * any deliverable ("Submit for Approval" trigger) create an approval
 * workflow pre-filled with its entity_type/entity_id, without duplicating
 * ApprovalsPage.tsx's/ClosePage.tsx's existing (near-identical) full forms.
 */

import { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { projectsApi, approvalsApi } from '../lib/api.js';
import { RoutingBuilder, type RoutingStep, type WorkflowType, type TeamMember } from './RoutingBuilder.js';

interface SubmitForApprovalPanelProps {
  projectId:  string;
  entityType: string;
  entityId:   string;
  title:      string;
  onCreated:  (approval: unknown) => void;
  onCancel:   () => void;
}

export function SubmitForApprovalPanel({
  projectId, entityType, entityId, title, onCreated, onCancel,
}: SubmitForApprovalPanelProps) {
  const { user, devHeaders } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [steps, setSteps]             = useState<RoutingStep[]>([]);
  const [wfType, setWfType]           = useState<WorkflowType>('sequential');
  const [submitting, setSubmitting]   = useState(false);
  const [err, setErr]                 = useState('');

  useEffect(() => {
    let cancelled = false;
    // Approvers must be members of THIS project — POST /approvals rejects anyone
    // else — so offer project members, not the whole workspace.
    projectsApi.members(projectId, devHeaders)
      .then((data) => { if (!cancelled) setTeamMembers(data as TeamMember[]); })
      .catch(() => { if (!cancelled) setTeamMembers([]); });
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (steps.length === 0) { setErr('Add at least one approver.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      const created = await approvalsApi.create({
        project_id:    projectId,
        requested_by:  user.userId,
        entity_type:   entityType,
        entity_id:     entityId,
        title,
        workflow_type: wfType,
        steps: steps.map((s, i) => ({
          approver_id:    s.approverId,
          step_order:     i + 1,
          parallel_group: s.parallelGroup,
          condition_expr: s.conditionExpr ?? null,
          is_required:    s.isRequired,
        })),
      }, devHeaders);
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create approval');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">{title}</h3>
        <button onClick={onCancel} className="btn-icon text-muted hover:text-ink">
          <X size={16} />
        </button>
      </div>

      <RoutingBuilder
        teamMembers={teamMembers}
        value={steps}
        workflowType={wfType}
        onChange={(s, t) => { setSteps(s); setWfType(t); }}
      />

      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-[rgba(8,17,31,0.06)]">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button onClick={() => void handleSubmit()} disabled={submitting} className="btn-primary text-sm">
          {submitting ? 'Submitting…' : 'Submit for Approval'}
        </button>
      </div>
    </div>
  );
}
